import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowsLeftRight, X } from "@phosphor-icons/react";

/**
 * Swap the VIN on a Booking or pre-RTO Sale without losing the customer,
 * payments, or audit linkage. Dialog is reused across BookingsPage and
 * SaleDetailDrawer — keeps the operator's mental model consistent in both
 * places.
 *
 * Backend constraints (mirrored in the dialog UI for fast feedback):
 *   - new VIN must be Available, same primary package, same branch
 *   - sale must not have a registration in progress
 */
export default function SwapVehicleDialog({
  open,
  onClose,
  sourceType,    // "booking" | "sale"
  sourceId,
  currentVehicleId,
  currentVin,
  onSwapped,
}) {
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [packageName, setPackageName] = useState("");
  const [pickedVehicleId, setPickedVehicleId] = useState("");
  const [reason, setReason] = useState("");
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Load the candidate list when the dialog opens. Server already restricts
  // by package + branch + availability so this is a single round-trip.
  useEffect(() => {
    if (!open || !currentVehicleId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/inventory/swap-candidates/${currentVehicleId}`);
        if (cancelled) return;
        setCandidates(data.candidates || []);
        setPackageName(data.package_name || "");
        setPickedVehicleId("");
        setReason("");
        setSearch("");
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, currentVehicleId]);

  if (!open) return null;

  const filtered = !search
    ? candidates
    : candidates.filter((c) => {
        const q = search.toLowerCase();
        return (
          (c.vin || "").toLowerCase().includes(q) ||
          (c.color || "").toLowerCase().includes(q) ||
          (c.engine_no || "").toLowerCase().includes(q) ||
          String(c.dealer_code || "").toLowerCase().includes(q)
        );
      });

  const submit = async () => {
    if (!pickedVehicleId) {
      toast.error("Pick a replacement vehicle first.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/inventory/swap-vehicle", {
        source_type: sourceType,
        source_id: sourceId,
        new_vehicle_id: pickedVehicleId,
        reason,
      });
      toast.success(`Swapped ${data.old_vin} → ${data.new_vin}`);
      onSwapped?.(data);
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      onClick={onClose}
      data-testid="swap-vehicle-dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-canvas-card border-2 border-ink p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto space-y-4"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-bold text-ink flex items-center gap-2">
              <ArrowsLeftRight size={18} weight="bold" />
              Swap Vehicle
            </h3>
            <p className="text-xs text-ink-muted mt-1">
              Replace the current VIN with another unit of the same package.
              Customer, payments, and audit trail are preserved.
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-canvas" data-testid="swap-close">
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="bg-canvas border border-canvas-line p-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Current VIN</span>
            <span className="font-mono text-ink font-semibold">{currentVin || "—"}</span>
          </div>
          {packageName && (
            <div className="flex items-center justify-between mt-1">
              <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Package</span>
              <span className="text-ink">{packageName}</span>
            </div>
          )}
        </div>

        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
            Replacement VIN — same package, same branch, available only
          </label>
          <input
            type="search"
            placeholder="Filter by VIN, color, engine no, dealer code…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="swap-search"
            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm font-mono mb-2 focus:outline-none focus:border-ink"
          />
          <div className="border border-canvas-line max-h-64 overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center text-sm text-ink-muted">Loading candidates…</div>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-center text-sm text-ink-muted">
                No matching Available vehicles in this branch + package.
              </div>
            ) : (
              filtered.map((c) => (
                <label
                  key={c.id}
                  className={`flex items-center gap-3 px-3 py-2 border-b border-canvas-line cursor-pointer hover:bg-canvas ${
                    pickedVehicleId === c.id ? "bg-brand/5 border-brand" : ""
                  }`}
                  data-testid={`swap-candidate-${c.id}`}
                >
                  <input
                    type="radio"
                    name="swap-pick"
                    checked={pickedVehicleId === c.id}
                    onChange={() => setPickedVehicleId(c.id)}
                  />
                  <div className="flex-1 grid grid-cols-4 gap-2 text-xs">
                    <div className="font-mono text-ink font-semibold">{c.vin}</div>
                    <div className="text-ink-muted">{c.color || "—"}</div>
                    <div className="font-mono text-ink-muted">{c.engine_no || "—"}</div>
                    <div className="font-mono text-ink-muted">{c.dealer_code ?? "—"}</div>
                  </div>
                </label>
              ))
            )}
          </div>
        </div>

        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
            Reason (optional)
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="e.g. scratched in transit, RTO rejected chassis…"
            data-testid="swap-reason"
            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm border border-canvas-line hover:border-ink"
            data-testid="swap-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting || !pickedVehicleId}
            data-testid="swap-confirm"
            className="px-4 py-2 text-sm bg-ink text-canvas hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? "Swapping…" : "Confirm Swap"}
          </button>
        </div>
      </div>
    </div>
  );
}
